import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { MsalProvider } from '@azure/msal-react';
import {
  EventType,
  PublicClientApplication,
  type AuthenticationResult,
  type EventMessage,
} from '@azure/msal-browser';
import { msalConfig } from '../lib/authConfig.js';

/**
 * Singleton MSAL instance. Attached to `window` so HMR and the React strict
 * double-mount don't create duplicate instances.
 */
const MSAL_INSTANCE_KEY = '__avl_msal_instance__';

function getInstance(): PublicClientApplication | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as Record<string, PublicClientApplication | undefined>;
  if (w[MSAL_INSTANCE_KEY]) return w[MSAL_INSTANCE_KEY] as PublicClientApplication;
  const instance = new PublicClientApplication(msalConfig);
  instance.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      instance.setActiveAccount((event.payload as AuthenticationResult).account);
    }
  });
  w[MSAL_INSTANCE_KEY] = instance;
  return instance;
}

let initPromise: Promise<void> | null = null;

function initializeOnce(instance: PublicClientApplication): Promise<void> {
  if (initPromise) return initPromise;
  initPromise = instance
    .initialize()
    .then(() => instance.handleRedirectPromise())
    .then((response) => {
      if (response) instance.setActiveAccount(response.account);
    })
    .catch((err) => {
      console.error('MSAL init failed:', err);
    });
  return initPromise;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [instance] = useState<PublicClientApplication | null>(() => getInstance());
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!instance) return;
    initializeOnce(instance).then(() => setReady(true));
  }, [instance]);

  if (!ready || !instance) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div
          className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid"
          style={{ borderColor: 'var(--accent)', borderRightColor: 'transparent' }}
          aria-label="Loading authentication"
        />
      </div>
    );
  }

  return <MsalProvider instance={instance}>{children}</MsalProvider>;
}
